import { SELECTION_MARKET, type GameSelection, type LightningOffer, type LightningPolicy, type OddsLine } from '@urbanrats/contracts';
import { canonicalJson, hexToBytes, hmacSha256Hex, sha256Hex } from './hash.ts';

/** Source of cryptographically secure random bytes (browser/Node `crypto.getRandomValues`; fixed in tests). */
export interface RandomSource {
  bytes(n: number): Uint8Array;
}

export const cryptoRandomSource: RandomSource = {
  bytes(n) {
    const b = new Uint8Array(n);
    globalThis.crypto.getRandomValues(b);
    return b;
  },
};

export const lightningCommitment = (serverSeedHex: string): string => sha256Hex(hexToBytes(serverSeedHex));

/** Selections a policy may boost, in manifest order (the order is part of the verifiable draw). */
export function lightningPool(odds: readonly OddsLine[], policy: LightningPolicy): GameSelection[] {
  return odds.filter((o) => policy.eligibleMarkets.includes(SELECTION_MARKET[o.selection])).map((o) => o.selection);
}

/**
 * Deterministic uniform integers in [0, n) from HMAC-SHA256(seed, "<roundId>|<policyVersion>|<counter>"),
 * 32-bit words with rejection sampling (no modulo bias).
 */
function uniform(seed: Uint8Array, roundId: string, policyVersion: string): (n: number) => number {
  let counter = 0;
  let words: number[] = [];
  const next = (): number => {
    if (words.length === 0) {
      const h = hmacSha256Hex(seed, `${roundId}|${policyVersion}|${counter++}`);
      words = Array.from({ length: 8 }, (_, i) => parseInt(h.slice(i * 8, i * 8 + 8), 16));
    }
    return words.shift()!;
  };
  return (n) => {
    if (!Number.isSafeInteger(n) || n <= 0 || n > 0xffffffff) throw new RangeError('bad range');
    const limit = 0x100000000 - (0x100000000 % n);
    for (;;) {
      const w = next();
      if (w < limit) return w % n;
    }
  };
}

/**
 * Lightning draw for one round: distinct selections from the eligible pool, each with a tier drawn by
 * weight. Pure function of (seed, round id, policy, pool) so anyone can recompute it after the reveal.
 * It only multiplies payouts; it never touches the count or the result.
 */
export function drawLightning(serverSeedHex: string, gameRoundId: string, policy: LightningPolicy, pool: readonly GameSelection[]): LightningOffer {
  const seed = hexToBytes(serverSeedHex);
  if (seed.length !== 32) throw new RangeError('server seed must be 32 bytes');
  const rnd = uniform(seed, gameRoundId, policy.policyVersion);
  const remaining = [...pool];
  const total = policy.tiers.reduce((a, t) => a + t.weight, 0);
  const boosts: LightningOffer['boosts'] = [];
  for (let i = 0; i < Math.min(policy.selectionsPerRound, remaining.length); i++) {
    const selection = remaining.splice(rnd(remaining.length), 1)[0]!;
    let r = rnd(total);
    const tier = policy.tiers.find((t) => (r -= t.weight) < 0)!;
    boosts.push({ selection, tier: tier.tier, factor: tier.factor });
  }
  return { policyVersion: policy.policyVersion, commitment: lightningCommitment(serverSeedHex), boosts };
}

/** Independent check after the reveal: commitment matches and the published boosts are the draw's output. */
export function verifyLightning(offer: LightningOffer, serverSeedHex: string, gameRoundId: string, policy: LightningPolicy, pool: readonly GameSelection[]): boolean {
  if (lightningCommitment(serverSeedHex) !== offer.commitment) return false;
  // Key order is not significant (offers pass through JSON stores such as PostgreSQL jsonb).
  return canonicalJson(drawLightning(serverSeedHex, gameRoundId, policy, pool)) === canonicalJson(offer);
}
